import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import DocumentCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentList/DocumentCard';
import Filters from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentList/Filters';
import useEffectiveDocumentFilters from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentList/Hooks/useEffectiveDocumentFilters';
import useSubjectDocuments from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Hooks/useSubjectDocuments';
import PlainDate from '@Domain/PlainDate';
import { css } from '@emotion/react';
import { FileText } from 'lucide-react';
import * as React from 'react';
import { Skeleton } from '@verclam/ui-kit';

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	width: 100%;
	max-width: 51.25rem;
`;

const titleStyle = css`
	${theme.typography.pageTitle}
	margin: 0;
	color: ${theme.colors.ink};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		font-size: 2rem;
	}
`;

const listStyle = css`
	display: flex;
	flex-direction: column;
	margin: 0;
	padding: 0.375rem 1.25rem;
	list-style: none;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};
`;

const skeletonStackStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

type Props = {
	readonly subject: SubjectAddress;
};

/**
 * Every document of the dossier, verified or not. The type and period filters narrow the list on the
 * client side only.
 */
const DocumentListContent: React.FC<Props> = ({ subject }) => {
	const filters = useEffectiveDocumentFilters(subject);

	const { data: documents, error, refetch } = useSubjectDocuments(subject);

	const filteredDocuments = React.useMemo(() => {
		const today = PlainDate.today();

		return documents?.list.filter((document) => filters.matches(document, today)) ?? [];
	}, [documents, filters]);

	// First render, and every render before the fetch has ever had a chance to resolve: never the
	// error state (`guides/state-stores.md`, "Ce que `useStoreQuery` ne garantit pas").
	if (documents === null && error === null) {
		return (
			<section css={sectionStyle}>
				<h1 css={titleStyle}>Documents du dossier</h1>
				<div css={skeletonStackStyle}>
					<Skeleton height="3.5rem" animation="wave" shape="rectangle" />
					<Skeleton height="3.5rem" animation="wave" shape="rectangle" />
				</div>
			</section>
		);
	}

	if (error !== null) {
		return (
			<section css={sectionStyle}>
				<h1 css={titleStyle}>Documents du dossier</h1>
				<ErrorState title="Impossible de charger les documents." onRetry={refetch} />
			</section>
		);
	}

	if (documents === null || documents.list.length === 0) {
		return (
			<section css={sectionStyle}>
				<h1 css={titleStyle}>Documents du dossier</h1>
				<EmptyState title="Aucun document" icon={<FileText size={28} />} />
			</section>
		);
	}

	return (
		<section css={sectionStyle}>
			<h1 css={titleStyle}>Documents du dossier</h1>
			<Filters documents={documents.list} />
			<ConditionalLoader
				data={filteredDocuments.length > 0}
				fallback={
					<EmptyState
						title="Aucun document ne correspond aux filtres"
						icon={<FileText size={28} />}
					/>
				}
			>
				<ul css={listStyle}>
					{filteredDocuments.map((document) => (
						<DocumentCard key={document.id} subject={subject} document={document} />
					))}
				</ul>
			</ConditionalLoader>
		</section>
	);
};

export default DocumentListContent;
